import { Upload, FileText, Edit, Trash2, Eye, EyeOff, CheckCircle2, AlertCircle, ArrowLeft, ShieldCheck, Loader2, BarChart3 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useDefaultWaiverAvailable, useApplyDefaultWaiver } from '../../hooks/useDefaultWaiver';
import { useCompanyWaiverTemplates, useDeleteTemplate, useUpdateTemplate, useWaiverComplianceStats } from '../../hooks/useWaivers';
import { listCompanyWaiverTemplates } from '../../services/damageWaivers';
import type { DamageWaiverTemplate } from '../../types/damageWaiver.types';

export default function DamageWaiversPage() {
  const navigate = useNavigate();
  const { data: currentCompanyId, isLoading: isLoadingCompany } = useCurrentCompany();
  const { data: templates, isLoading, error } = useCompanyWaiverTemplates(currentCompanyId);
  const deleteMutation = useDeleteTemplate();
  const updateMutation = useUpdateTemplate();
  const { data: defaultWaiver } = useDefaultWaiverAvailable();
  const { apply: applyDefaultWaiver, isLoading: isApplyingDefault } = useApplyDefaultWaiver();
  const { data: compliance } = useWaiverComplianceStats(currentCompanyId);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [localTemplates, setLocalTemplates] = useState<DamageWaiverTemplate[] | undefined>(templates);

  // Sync local templates with hook data
  useEffect(() => {
    setLocalTemplates(templates);
  }, [templates]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleUseDefault = async () => {
    if (!currentCompanyId) return;
    try {
      await applyDefaultWaiver(currentCompanyId);
      showNotification('success', 'Default waiver template applied successfully!');
      listCompanyWaiverTemplates(currentCompanyId).then(setLocalTemplates).catch(() => {});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply default waiver';
      showNotification('error', message);
    }
  };

  const handleUpload = () => {
    navigate('/damage-waivers/upload');
  };

  const handleEditFields = (templateId: string) => {
    navigate(`/damage-waivers/${templateId}/fields`);
  };

  const handleToggleActive = async (templateId: string, isActive: boolean) => {
    // Optimistically update the UI immediately
    setLocalTemplates((prev) => 
      prev?.map((template) =>
        template.id === templateId
          ? { ...template, is_active: !isActive }
          : template
      ) ?? prev
    );

    try {
      await updateMutation.update({
        templateId,
        updates: { is_active: !isActive },
      });
      showNotification('success', `Template ${!isActive ? 'activated' : 'deactivated'} successfully.`);
      // Optionally refetch to ensure consistency
      if (templates && currentCompanyId) {
        listCompanyWaiverTemplates(currentCompanyId).then(setLocalTemplates).catch(() => {
          // If refetch fails, revert optimistic update
          setLocalTemplates(templates);
        });
      }
    } catch {
      // Revert optimistic update on error
      setLocalTemplates(templates);
      showNotification('error', 'Failed to update template status.');
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!currentCompanyId) return;

    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    // Optimistically remove from UI
    const previousTemplates = localTemplates;
    setLocalTemplates((prev) => prev?.filter((t) => t.id !== templateId) ?? prev);

    try {
      await deleteMutation.remove({ templateId, companyId: currentCompanyId });
      showNotification('success', 'Template deleted successfully.');
      // Refetch to ensure consistency
      listCompanyWaiverTemplates(currentCompanyId).then(setLocalTemplates).catch(() => {
        // If refetch fails, revert optimistic update
        setLocalTemplates(previousTemplates);
      });
    } catch {
      // Revert optimistic update on error
      setLocalTemplates(previousTemplates);
      showNotification('error', 'Failed to delete template.');
    }
  };

  if (isLoadingCompany || isLoading) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
          <p className="mt-2 text-[#A9A3A3]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 text-lg mb-2">Failed to load templates</p>
          <p className="text-[#A9A3A3] text-sm mb-4">{error instanceof Error ? error.message : 'Unknown error'}</p>
          <Button onClick={() => window.location.reload()} className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-5 max-w-sm ${
              notification.type === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="font-medium text-sm sm:text-base flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-70 transition-opacity text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}

        {/* Page Header - matches Dashboard styling */}
        <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Liability Waivers</h1>
              <p className="text-white/80 mt-1">Manage waiver templates for your gym</p>
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Content - fills remaining area (layout provides horizontal padding) */}
        <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 py-6 w-full">

          {/* Compliance summary card */}
          {compliance && (compliance.adults.total > 0 || compliance.children.total > 0) && (
            <Card className="bg-[#221F1F] border-[#504A4A] mb-6">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-[#9B1421] shrink-0" />
                    <div>
                      <p className="text-[#EBFFEE] font-medium text-sm">Compliance Overview</p>
                      <p className="text-[#A9A3A3] text-xs mt-0.5">
                        {compliance.adults.total > 0 && (
                          <span>Adults: {compliance.adults.signed}/{compliance.adults.total} signed</span>
                        )}
                        {compliance.adults.total > 0 && compliance.children.total > 0 && (
                          <span className="mx-2">·</span>
                        )}
                        {compliance.children.total > 0 && (
                          <span>Minors: {compliance.children.signed}/{compliance.children.total} signed</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {compliance.adults.total > 0 && (
                      <div className="flex flex-col items-end gap-1">
                        <div className="w-32 h-2 bg-[#363232] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${Math.round((compliance.adults.signed / compliance.adults.total) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-[#A9A3A3]">
                          {Math.round((compliance.adults.signed / compliance.adults.total) * 100)}% adults
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => navigate(ROUTES.DAMAGE_WAIVERS_COMPLIANCE)}
                      className="text-xs text-[#9B1421] hover:text-[#C41B2E] transition-colors whitespace-nowrap"
                    >
                      Full report →
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="mb-6">
            <Button
              onClick={handleUpload}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-4 sm:px-6 py-2 flex items-center justify-center gap-2 w-fit"
            >
              <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Upload New Waiver</span>
            </Button>
          </div>

        {/* Templates List */}
        {localTemplates && localTemplates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {localTemplates.map((template) => (
              <Card key={template.id} className="bg-[#221F1F] border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-[#EBFFEE] mb-1 truncate text-base sm:text-lg" title={template.name}>
                        {template.name}
                      </CardTitle>
                      {template.description && (
                        <p className="text-[#A9A3A3] text-xs sm:text-sm mt-2 line-clamp-2">{template.description}</p>
                      )}
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                      {template.is_active ? (
                        <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
                          Active
                        </span>
                      ) : (
                        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
                          Inactive
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                        (template as { template_type?: string }).template_type === 'html'
                          ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30'
                          : 'bg-[#504A4A] text-[#A9A3A3]'
                      }`}>
                        {(template as { template_type?: string }).template_type === 'html' ? 'Text' : 'PDF'}
                      </span>
                      {(() => {
                        const cat = (template as any).waiver_category as string | undefined;
                        const label = cat === 'adult' ? 'Adult' : cat === 'child' ? 'Minor' : cat === 'family' ? 'Family' : null;
                        if (!label) return null;
                        const bg = cat === 'family' ? 'bg-purple-600' : cat === 'child' ? 'bg-yellow-600' : 'bg-blue-600';
                        return <span className={`${bg} text-white text-xs px-2 py-1 rounded-full whitespace-nowrap`}>{label}</span>;
                      })()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
                    <Button
                      onClick={() => handleToggleActive(template.id, template.is_active)}
                      disabled={updateMutation.isLoading}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1.5 text-xs sm:text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 flex-1 sm:flex-initial"
                    >
                      {template.is_active ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Activate
                        </>
                      )}
                    </Button>
                    {(template as { template_type?: string }).template_type !== 'html' && (
                      <Button
                        onClick={() => handleEditFields(template.id)}
                        className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1.5 text-xs sm:text-sm flex items-center justify-center gap-1.5 flex-1 sm:flex-initial"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Fields
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDelete(template.id)}
                      disabled={deleteMutation.isLoading}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs sm:text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 flex-1 sm:flex-initial"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  </div>
                  <div className="text-xs text-[#A9A3A3] border-t border-[#504A4A] pt-3">
                    Created: {new Date(template.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="py-12 text-center">
              <ShieldCheck className="w-16 h-16 text-[#A9A3A3] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#EBFFEE] mb-2">No waiver templates yet</h3>
              <p className="text-[#A9A3A3] mb-6">Set up a liability waiver for your members to sign</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                {defaultWaiver && (
                  <Button
                    onClick={handleUseDefault}
                    disabled={isApplyingDefault}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isApplyingDefault ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        Use RollOps Default Waiver
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={handleUpload}
                  className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-6 py-3 flex items-center justify-center gap-2 border border-[#504A4A]"
                >
                  <Upload className="w-5 h-5" />
                  Upload Your Own Waiver
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
  );
}

